import { Temporal } from 'temporal-polyfill';
import { FormulaError, isFormulaError, ValueType } from './types';

/** One item of a list field: a record whose members are read by key. */
export type ListItem = Record<string, unknown>;

/**
 * What a formula evaluates to at the boundary: a number, text or TRUE/FALSE.
 * A date leaves the engine as ISO text (YYYY-MM-DD); inside it is a
 * Temporal.PlainDate.
 */
export type FormulaValue = number | string | boolean;

/**
 * A blank field mid-evaluation. Blank is missing data: arithmetic, ordering
 * and every function that takes values propagate it (a blank in, a blank
 * out, TEXT included), = and <> treat it as a value equal only to itself,
 * & reads it as "", and IFBLANK gives it a value. Never escapes: the public
 * evaluate() maps a blank result to null, so a formula with nothing to say
 * renders as an empty cell rather than as 0.
 */
export const BLANK = Symbol('blank');
export type EvalValue = number | string | boolean | Temporal.PlainDate | typeof BLANK;

/** A field's (or list member's) raw value as an EvalValue — same rules whichever record it came from. */
export function toValue(value: unknown, ref: string): EvalValue | FormulaError {
  // An empty cell is missing data, not the empty string — the same answer
  // whether the field is text, a date or a number. Only what the row supplies
  // is normalized; a "" the formula itself produces (a literal, or & over
  // blanks) keeps its meaning, which is Excel's own distinction: an empty cell
  // + 1 is 1, while ="" + 1 is #VALUE!.
  if (value === null || value === undefined || value === '') {
    return BLANK;
  }
  if (isFormulaError(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return new FormulaError(
      '#VALUE!',
      `Field [${ref}] is a list — it can only be used with REDUCE, SUM, SIZE or INDEX`
    );
  }
  if (typeof value === 'string' || typeof value === 'boolean' || value instanceof Temporal.PlainDate) {
    return value;
  }
  if (typeof value !== 'number') {
    return new FormulaError('#VALUE!', `Field [${ref}] is not a number, text, TRUE/FALSE or a date`);
  }
  if (Number.isNaN(value)) {
    // NaN is never a value anyone meant — it is how missing data arrives in a
    // computed number, so it reads as blank rather than as an error.
    return BLANK;
  }
  return Number.isFinite(value) ? value : new FormulaError('#NUM!', `Field [${ref}] is not a finite number`);
}

export function finiteOrNumError(value: number): number | FormulaError {
  return Number.isFinite(value) ? value : new FormulaError('#NUM!');
}

/**
 * The number a piece of text spells: entirely numeric ("5", " 2.5 ", "1e3"),
 * or #VALUE!. What VALUE() does — the only place text becomes a number.
 */
export function parseNumber(text: string): number | FormulaError {
  const trimmed = text.trim();
  if (trimmed === '' || !/^[-+]?(\d+\.?\d*|\d*\.\d+)([eE][-+]?\d+)?$/.test(trimmed)) {
    return new FormulaError('#VALUE!', `"${text}" is not a number`);
  }
  return Number(trimmed);
}

/**
 * A value where arithmetic needs a number: numbers pass through and text is
 * #VALUE! — the runtime mirror of the type rule, so a stored formula that
 * predates it degrades to an error rather than a guessed number. A blank is
 * 0 here for the one caller that adds blanks up as nothing (SUM); operators
 * never reach this with a blank, they propagate it first.
 */
export function requireNumber(value: EvalValue | FormulaError): number | FormulaError {
  if (value === BLANK) {
    return 0;
  }
  if (isFormulaError(value) || typeof value === 'number') {
    return value;
  }
  return new FormulaError(
    '#VALUE!',
    `"${value}" is text, not a number — use VALUE() to read a number from it`
  );
}

/** A value as text: a number's digits, TRUE/FALSE, a date as YYYY-MM-DD, a blank as "". */
export function toText(value: EvalValue): string {
  if (value === BLANK) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }
  return typeof value === 'string' ? value : String(value);
}

/** A value where a condition needs TRUE/FALSE: anything else is #VALUE!, the runtime mirror of the type rule. */
export function requireBoolean(value: EvalValue | FormulaError): boolean | FormulaError {
  if (isFormulaError(value) || typeof value === 'boolean') {
    return value;
  }
  return new FormulaError('#VALUE!', `${describeValue(value)} is not TRUE/FALSE`);
}

/** How a runtime value reads in an error message. */
export function describeValue(value: EvalValue): string {
  if (value === BLANK) {
    return 'A blank';
  }
  if (typeof value === 'string') {
    return `"${value}"`;
  }
  if (value instanceof Temporal.PlainDate) {
    return `The date ${value.toString()}`;
  }
  return String(value);
}

/** The type a runtime value has, for a message or a check. */
export function typeOfValue(value: Exclude<EvalValue, typeof BLANK>): ValueType {
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'string') {
    return 'string';
  }
  return typeof value === 'boolean' ? 'boolean' : 'date';
}

type Scalar = Exclude<EvalValue, typeof BLANK>;

/** Whether two values are equal: same type, and text case-insensitively. Never across types. */
export function valuesEqual(left: Scalar, right: Scalar): boolean {
  if (typeof left === 'boolean' || typeof right === 'boolean') {
    return left === right;
  }
  const cmp = compareValues(left, right);
  return !isFormulaError(cmp) && cmp === 0;
}

/**
 * Ordering: numbers numerically, text case-insensitively, dates by the
 * calendar. Two values of different types do not order, and TRUE/FALSE never
 * does — #VALUE!, the runtime mirror of the type rule (they may still be
 * compared with =, which is simply FALSE).
 */
export function compareValues(left: Scalar, right: Scalar): number | FormulaError {
  if (typeof left === 'number' && typeof right === 'number') {
    return left === right ? 0 : left < right ? -1 : 1;
  }
  if (typeof left === 'string' && typeof right === 'string') {
    const a = left.toLowerCase();
    const b = right.toLowerCase();
    return a === b ? 0 : a < b ? -1 : 1;
  }
  if (left instanceof Temporal.PlainDate && right instanceof Temporal.PlainDate) {
    return Temporal.PlainDate.compare(left, right);
  }
  return new FormulaError(
    '#VALUE!',
    typeOfValue(left) === typeOfValue(right)
      ? 'TRUE/FALSE cannot be ordered'
      : `${describeType(typeOfValue(left))} and ${describeType(typeOfValue(right))} cannot be ordered against each other`
  );
}

function describeType(type: ValueType): string {
  return type === 'number'
    ? 'A number'
    : type === 'string'
      ? 'Text'
      : type === 'boolean'
        ? 'TRUE/FALSE'
        : 'A date';
}

/** Excel ROUND: half away from zero, negative digits round to tens/hundreds/... */
export function round(value: number, digits: number): number | FormulaError {
  const factor = Math.pow(10, Math.trunc(digits));
  if (!Number.isFinite(factor) || factor === 0) {
    return new FormulaError('#NUM!');
  }
  return finiteOrNumError((Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor);
}
